import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Fingerprint, Users, ListOrdered, CalendarDays, Clock,
  BarChart3, Terminal, ScrollText, FileCheck2, Settings2, Wifi, ListTodo,
} from 'lucide-react';
import { ROUTES } from '@/routes/paths';
import { DeviceList } from '../components/DeviceList';
import { EmployeeEnrollTable } from '../components/EmployeeEnrollTable';
import { UnmappedPinsPanel } from '../components/UnmappedPinsPanel';
import { BiometricFullSettingsPanel } from '../components/BiometricFullSettings';
import { PunchLogTable } from '../components/PunchLogTable';
import { MusterRollGrid } from '../components/MusterRollGrid';
import { ShiftEditor } from '../components/ShiftEditor';
import { RosterEditor } from '../components/RosterEditor';
import { CommandQueuePanel } from '../components/CommandQueuePanel';
import { DeviceOpLogPanel } from '../components/DeviceOpLogPanel';
import { BiometricReports } from '../components/BiometricReports';
import { RegularisationPanel } from '../components/RegularisationPanel';
import { StatTile } from '../components/StatTile';
import { useBiometricAccess, useBiometricLiveUpdates, useDevices } from '../hooks/useBiometricAdmin';

const TABS = [
  { id: 'devices', label: 'Devices', icon: Fingerprint, devicesOnly: true },
  { id: 'employees', label: 'Employees', icon: Users, devicesOnly: true },
  { id: 'punches', label: 'Punches', icon: ListOrdered, devicesOnly: false },
  { id: 'attendance', label: 'Attendance', icon: CalendarDays, devicesOnly: false },
  { id: 'shifts', label: 'Shifts & Rosters', icon: Clock, devicesOnly: false },
  { id: 'regularisations', label: 'Regularisations', icon: FileCheck2, devicesOnly: false },
  { id: 'reports', label: 'Reports', icon: BarChart3, devicesOnly: false },
  { id: 'commands', label: 'Commands', icon: Terminal, devicesOnly: true },
  { id: 'logs', label: 'Logs', icon: ScrollText, devicesOnly: true },
  { id: 'settings', label: 'Settings', icon: Settings2, devicesOnly: true },
] as const;

type TabId = (typeof TABS)[number]['id'];

/** Admin console for the push-based (ADMS) biometric platform. */
export function BiometricAdminPage() {
  const { data: access } = useBiometricAccess();
  const canManageDevices = access?.canManageDevices ?? false;
  // Managers outside HR see attendance, punches and reports for their people; device administration is HR's.
  const [chosen, setTab] = useState<TabId | null>(null);
  const visibleTabs = TABS.filter((t) => canManageDevices || !t.devicesOnly);
  const tab: TabId = chosen && visibleTabs.some((t) => t.id === chosen) ? chosen : canManageDevices ? 'devices' : 'attendance';
  useBiometricLiveUpdates();
  const { data: devices = [] } = useDevices();

  // Real, derived-only fleet summary — folded over the same device list the
  // Devices tab already fetches (shared query cache, no extra request).
  const deviceStats = useMemo(() => {
    let online = 0;
    let enrolled = 0;
    let pendingCommands = 0;
    for (const d of devices) {
      if (d.online) online++;
      enrolled += d.user_count ?? 0;
      pendingCommands += d.pending_commands ?? 0;
    }
    return { total: devices.length, online, enrolled, pendingCommands };
  }, [devices]);

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <header className="flex items-start gap-3">
        <Link to={ROUTES.attendance} aria-label="Back to Attendance" className="mt-1 rounded-full p-2 text-on-surface-variant hover:bg-surface-variant">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-on-surface">
            <Fingerprint className="h-6 w-6 text-primary" />
            Biometric Administration
          </h1>
          <p className="mt-1 text-sm text-on-surface-variant">
            Machines push punches and enrolments to this server over the internet, or are read over the office network.
            Everything eSSL&apos;s Windows software did lives here.
          </p>
        </div>
      </header>

      {/* Fleet summary stats */}
      {devices.length > 0 && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatTile icon={Fingerprint} label="Devices" value={deviceStats.total} tone="primary" index={0} />
          <StatTile icon={Wifi} label="Online Now" value={deviceStats.online} tone="success" index={1} />
          <StatTile icon={Users} label="Enrolled Users" value={deviceStats.enrolled} tone="info" index={2} />
          <StatTile icon={ListTodo} label="Pending Commands" value={deviceStats.pendingCommands} tone="warning" index={3} />
        </div>
      )}

      <nav className="flex flex-wrap gap-1 border-b border-outline-variant" role="tablist">
        {visibleTabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
              tab === id
                ? 'border-primary text-primary'
                : 'border-transparent text-on-surface-variant hover:text-on-surface'
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </nav>

      <div role="tabpanel">
        {tab === 'devices' && <DeviceList />}
        {tab === 'employees' && (
          <div className="space-y-4">
            <UnmappedPinsPanel />
            <EmployeeEnrollTable />
          </div>
        )}
        {tab === 'punches' && <PunchLogTable />}
        {tab === 'attendance' && <MusterRollGrid />}
        {tab === 'shifts' && (
          <div className="space-y-4">
            <ShiftEditor />
            <RosterEditor />
          </div>
        )}
        {tab === 'regularisations' && <RegularisationPanel />}
        {tab === 'reports' && <BiometricReports />}
        {tab === 'commands' && <CommandQueuePanel />}
        {tab === 'logs' && <DeviceOpLogPanel />}
        {tab === 'settings' && <BiometricFullSettingsPanel />}
      </div>
    </div>
  );
}

export default BiometricAdminPage;
